import React from "react";
import { Link } from "react-router-dom";
import SiteShell from "../components/layout/SiteShell";
import SEO from "../components/seo/SEO";
import EbayReviewsCarousel from "../components/reviews/EbayReviewsCarousel";
import EbaySellerCriteriaStrip from "../components/reviews/EbaySellerCriteriaStrip";
import EbayReviewsExpandableList from "../components/reviews/EbayReviewsExpandableList";
import {
  MARKETPLACE_TRUST_NOTE,
  REVIEWS_DISCLAIMER,
} from "../lib/reviewsTrust";
import {
  EBAY_FEEDBACK_SOURCE_URL,
  EBAY_POSITIVE_COUNT,
} from "../lib/ebayReviews";
import { HOME_CTA_DARK_OUTLINE } from "../lib/homeCta";
import { getLifestyleImage } from "../lib/lifestyleImages";

const HERO_IMG = getLifestyleImage("cleanSceneryCoast");
const GOLD_JAR = "/images/products/gold-sea-moss-jar.cutout.png";
const PURPLE_JAR = "/images/products/purple-sea-moss-jar.cutout.png";

export default function ReviewsPage() {
  const heroSrc =
    HERO_IMG?.src || "/images/lifestyle/clean-scenery-coast.png";
  const heroAlt =
    HERO_IMG?.alt ||
    "Bright tropical coastline with clear turquoise water — coastal freshness";

  return (
    <SiteShell theme="dark">
      <SEO
        title="Buyer feedback | Moss Matrix"
        description={`Positive eBay feedback for Moss Matrix — ${EBAY_POSITIVE_COUNT} positives in the last 12 months, with buyer quotes and what they purchased.`}
        path="/reviews"
      />

      {/* Full-bleed coastal hero + gold/purple jar collage; soft fade into reviews */}
      <header className="relative min-h-[100svh] w-full overflow-hidden mm-reviews-hero">
        <img
          src={heroSrc}
          alt={heroAlt}
          className="absolute inset-0 h-full w-full object-cover object-[58%_42%] md:object-[62%_40%] brightness-[1.04] contrast-[1.02]"
          width={HERO_IMG?.width || 1600}
          height={HERO_IMG?.height || 1067}
          fetchPriority="high"
        />
        <div
          className="absolute inset-0 bg-gradient-to-b from-[#0c1410]/25 via-transparent to-transparent"
          aria-hidden
        />
        <div
          className="absolute inset-x-0 bottom-0 h-[38%] bg-gradient-to-t from-[#0c1410] via-[#0c1410]/65 to-transparent"
          aria-hidden
        />
        <div
          className="absolute inset-y-0 left-0 w-[38%] max-w-lg bg-gradient-to-r from-[#0c1410]/50 via-[#0c1410]/15 to-transparent"
          aria-hidden
        />

        {/* Product jars — right / open water, soft fade-in + gentle float */}
        <div
          className="mm-reviews-hero__jars pointer-events-none absolute inset-0 z-[5]"
          aria-hidden
        >
          <div className="mm-reviews-hero__jar-slot mm-reviews-hero__jar-slot--gold">
            <img
              src={GOLD_JAR}
              alt=""
              className="mm-reviews-hero__jar"
              width={512}
              height={512}
              decoding="async"
            />
          </div>
          <div className="mm-reviews-hero__jar-slot mm-reviews-hero__jar-slot--purple">
            <img
              src={PURPLE_JAR}
              alt=""
              className="mm-reviews-hero__jar"
              width={512}
              height={512}
              decoding="async"
            />
          </div>
        </div>

        <div className="relative z-10 flex min-h-[100svh] flex-col justify-end px-6 md:px-10 pb-14 md:pb-20 pt-28">
          <div className="max-w-7xl mx-auto w-full">
            <p className="home-eyebrow m-0 mb-2 text-sage/80">Moss Matrix</p>
            <h1 className="font-sans font-semibold text-2xl sm:text-3xl md:text-4xl text-ivory/95 m-0 tracking-tight max-w-sm leading-[1.15]">
              Buyer feedback
            </h1>
            <p className="mt-2.5 text-sm text-sage/85 m-0 max-w-sm leading-relaxed">
              {EBAY_POSITIVE_COUNT} positive on eBay in the last 12 months — real purchase quotes below.
            </p>
          </div>
        </div>
      </header>

      <main className="relative -mt-6 md:-mt-10 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 mm-dark-content">
          <p className="text-[11px] text-sage/70 max-w-2xl mb-8 m-0 leading-relaxed">
            {MARKETPLACE_TRUST_NOTE} {REVIEWS_DISCLAIMER}
          </p>

          <section className="mb-12" aria-labelledby="featured-ebay-heading">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
              <h2
                id="featured-ebay-heading"
                className="font-sans font-semibold text-2xl md:text-[1.65rem] text-ivory m-0"
              >
                Featured quotes
              </h2>
              <a
                href={EBAY_FEEDBACK_SOURCE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-sage/80 underline decoration-white/20 underline-offset-2 hover:text-goldhover"
              >
                See all feedback on eBay
              </a>
            </div>
            <EbayReviewsCarousel mode="full" showSourceNote={false} />
          </section>

          <EbaySellerCriteriaStrip className="mb-14" />

          <EbayReviewsExpandableList className="mb-14" />

          <section
            className="mb-10 rounded-3xl border border-white/14 bg-panel/80 p-8 md:p-10"
            aria-labelledby="website-reviews-next-heading"
          >
            <h2
              id="website-reviews-next-heading"
              className="font-sans font-semibold text-xl text-ivory mb-2"
            >
              Website reviews coming next
            </h2>
            <p className="text-sm text-sage max-w-2xl leading-relaxed m-0">
              MossMatrix.com reviews will be collected from direct customers after checkout, moderated,
              and shown separately from marketplace feedback above.
            </p>
          </section>

          <section
            className="mb-12 rounded-3xl border border-gold/25 bg-gradient-to-br from-gold/15 via-panel to-void px-6 py-10 md:px-10 text-center max-w-2xl mx-auto"
            aria-labelledby="share-story-heading"
          >
            <h2
              id="share-story-heading"
              className="font-sans font-semibold text-2xl text-ivory"
            >
              Share your story
            </h2>
            <p className="mt-3 text-sm text-sage leading-relaxed m-0 max-w-md mx-auto">
              Use Moss Matrix in your kitchen? Tell us how — we review stories before publishing.
            </p>
            <Link
              to="/stories#share-story"
              className="mt-6 inline-flex rounded-full bg-gold text-void px-8 py-3.5 text-sm font-medium hover:bg-goldhover transition"
            >
              Share your story
            </Link>
          </section>

          <div className="text-center">
            <Link to="/stories" className={`${HOME_CTA_DARK_OUTLINE} px-6 py-3 text-sm`}>
              Read website stories
            </Link>
          </div>
        </div>
      </main>
    </SiteShell>
  );
}
